'use client'

// One composer engine for the Social tab's Twetch composer. Owns the phase machine and the
// error branching; the components own only their copy and layout.
//
// The locked UX: ONE wallet popup per publish attempt. `submit` calls the
// wallet's signMessage exactly once; an expired/replayed signature does NOT
// silently re-sign — it lands in the `error` phase with `canRetry`, and the
// user's next explicit submit is a fresh attempt (fresh ts, fresh popup).

import React from 'react'
import useSwr from 'swr'
import Wallet from '@/utils/wallet'
import { bip322Capable } from './twetch-auth'
import { signMessageWithWallet } from './wallet-signature'
import { beginSocialFlow, socialFlowFailed, socialFlowSucceeded } from './social-telemetry'
import {
	isStaleSessionRefusal,
	recoverStaleSession,
	STALE_SESSION_MESSAGE
} from './twetch-session-recovery'
import { fetchSponsorEligibility, TWETCH_API } from './twetch-api'
import type { TwetchSmartTag } from './twetch-api'
import {
	classifyPostError,
	postFailureMessage,
	sendSponsoredPost,
	type PostFailure
} from './twetch-post'
import { sessionDeviceKey, useTwetch } from './twetch-store'
import { identityForAddress } from './identity-match'

type ComposerPhase = 'idle' | 'signing' | 'publishing' | 'posted'

interface ComposerError {
	kind: PostFailure | 'wallet-rejected'
	message: string
}

export interface SponsoredComposer {
	/// Null when nobody is signed in to Twetch.
	identity: ReturnType<typeof useTwetch.getState>['identity']
	/// True once the eligibility probe answered `eligible: true`. Null while
	/// the probe is in flight (the composer renders a quiet pending state,
	/// not a guess).
	eligible: boolean | null
	phase: ComposerPhase
	error: ComposerError | null
	/// The created post's permalink target, once posted.
	posted: { id?: number; txid?: string | null } | null
	submit: (
		content: string,
		smartTags?: TwetchSmartTag[],
		reply?: { postId: number; txid: string | null } | null,
		turnstileToken?: string | null,
		thesisTag?: TwetchSmartTag | null
	) => void
	reset: () => void
}

/// Which Twetch account this wallet address belongs to now, or null when the
/// lookup fails or the address has no named account (a bare session account
/// has no name and is not returned, so "no answer" never signs anyone out).
async function accountOwningAddress(address: string): Promise<number | null> {
	try {
		const res = await fetch(`${TWETCH_API}/v1/users/by-addresses`, {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({ addresses: [address] })
		})
		if (!res.ok) return null
		const body = (await res.json()) as { users?: Record<string, { id?: number }> }
		const id = body.users?.[address]?.id
		return typeof id === 'number' && Number.isFinite(id) ? id : null
	} catch {
		return null
	}
}

function isWalletRejection(e: unknown): boolean {
	const msg = String((e as { message?: string })?.message ?? '')
	return /reject|declin|cancel|denied/i.test(msg)
}

export default function useSponsoredPost(onPosted?: () => void): SponsoredComposer {
	const storedIdentity = useTwetch((s) => s.identity)
	const setIdentity = useTwetch((s) => s.setIdentity)
	const address = Wallet.getTaprootAddress()
	// A wallet switch must never keep posting through the previous wallet's
	// cached Twetch session. Treat it as a fresh first-post resolution; the API
	// will recover the account linked to the newly connected signer.
	const identity = identityForAddress(storedIdentity, address)
	const [phase, setPhase] = React.useState<ComposerPhase>('idle')
	const [error, setError] = React.useState<ComposerError | null>(null)
	const [posted, setPosted] = React.useState<SponsoredComposer['posted']>(null)

	// Keep transient feedback on the action itself long enough to be noticed,
	// then restore the normal compose state without leaving stale success or
	// failure copy in the form.
	React.useEffect(() => {
		if (phase !== 'posted' && error === null) return
		const timer = window.setTimeout(() => {
			setPhase('idle')
			setError(null)
			setPosted(null)
		}, 5_000)
		return () => window.clearTimeout(timer)
	}, [phase, error])

	const { data: eligibility } = useSwr(
		identity ? `twetch-eligibility-${identity.uid}-${identity.address}` : null,
		() =>
			fetchSponsorEligibility(identity!.uid, {
				algorithm: identity!.algorithm,
				address: identity!.address
			}),
		{ revalidateOnFocus: false }
	)

	// A merged account: the wallet's session account was folded into a real
	// Twetch account, which tombstones the old id and re-points the wallet at
	// the survivor. The cached identity still carries the old id, so the probe
	// says "not eligible" and the composer would sit at "Posting is paused"
	// for good; the post-time recovery never gets a chance to run. When the
	// wallet now resolves to a different account, drop the stale session and
	// let the first-post path open one on the survivor.
	const signOut = useTwetch((s) => s.signOut)
	React.useEffect(() => {
		if (!identity || eligibility?.eligible !== false) return
		let cancelled = false
		void (async () => {
			const owner = await accountOwningAddress(identity.address)
			if (cancelled || owner === null || owner === identity.uid) return
			console.info('[social] cached Twetch session belongs to a merged account; re-opening on the survivor', {
				stale_uid: identity.uid,
				owner
			})
			socialFlowFailed(
				beginSocialFlow('social_login', 'sign_login'),
				'sign_login',
				new Error(`session account ${identity.uid} merged into ${owner}`),
				'session_merged',
				{ address: identity.address }
			)
			signOut()
		})()
		return () => {
			cancelled = true
		}
	}, [identity, eligibility, signOut])

	const submit = React.useCallback(
		(
			content: string,
			smartTags?: TwetchSmartTag[],
			reply?: { postId: number; txid: string | null } | null,
			turnstileToken?: string | null,
			thesisTag?: TwetchSmartTag | null
		) => {
			if (!bip322Capable(address)) {
				setError({
					kind: 'identity-mismatch',
					message: 'Connect a mainnet bc1 wallet that can sign messages.'
				})
				return
			}
			const deviceKey = sessionDeviceKey(identity)
			setError(null)
			setPosted(null)
			setPhase('signing')
			const flow = beginSocialFlow('social_post', 'sign_post')
			let stage: 'sign_post' | 'publish_post' = 'sign_post'
			void (async () => {
				try {
					const send = (
						who: typeof identity,
						key: typeof deviceKey
					): ReturnType<typeof sendSponsoredPost> =>
						sendSponsoredPost({
							identity: who,
							deviceKey: key,
							address,
							content,
							smartTags,
							turnstileToken,
							thesisTag: thesisTag ?? null,
							reply: reply ?? null,
							// Signing in first (a thesis from a fresh browser) keeps the
							// 'Check wallet' phase; only the post's own signature ends it.
							signLogin: signMessageWithWallet,
							signWithWallet: async (text) => {
								const sig = await signMessageWithWallet(text)
								setPhase('publishing')
								stage = 'publish_post'
								return sig
							},
							onProvisioned: setIdentity
						})
					let result: Awaited<ReturnType<typeof sendSponsoredPost>>
					try {
						result = await send(identity, deviceKey)
					} catch (e) {
						// Session rotated by a sign-in on another device: sign in
						// again and publish once more, rather than a bare
						// "invalid signature" the user cannot act on.
						if (!isStaleSessionRefusal(e)) throw e
						socialFlowFailed(flow, stage, e, 'session_stale', { address, thesis: !!thesisTag })
						let fresh
						try {
							fresh = await recoverStaleSession(address)
						} catch {
							throw new Error(STALE_SESSION_MESSAGE)
						}
						setIdentity(fresh.identity)
						setPhase('signing')
						result = await send(fresh.identity, fresh.deviceKey)
					}
					const { post } = result
					socialFlowSucceeded(flow, 'publish_post')
					setPosted(post ?? {})
					setPhase('posted')
					onPosted?.()
				} catch (e) {
					if (isWalletRejection(e)) {
						socialFlowFailed(flow, stage, e, 'wallet_rejected', {
							address,
							thesis: !!thesisTag,
							cancelled: true
						})
						setError({
							kind: 'wallet-rejected',
							message: 'You cancelled in the wallet. Nothing was sent.'
						})
					} else {
						const kind = classifyPostError(e)
						socialFlowFailed(flow, stage, e, kind, { address, thesis: !!thesisTag })
						setError({
							kind,
							message: postFailureMessage(kind, String((e as Error)?.message ?? ''))
						})
					}
					setPhase('idle')
				}
			})()
		},
		[identity, address, onPosted, setIdentity]
	)

	const reset = React.useCallback(() => {
		setPhase('idle')
		setError(null)
		setPosted(null)
	}, [])

	return {
		identity,
		eligible: identity
			? eligibility
				? eligibility.eligible
				: null
			: bip322Capable(address)
			? true
			: false,
		phase,
		error,
		posted,
		submit,
		reset
	}
}
